import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeProvider';
import { AnalyticsProvider } from './components/AnalyticsProvider';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AccessibilityMenu } from './components/AccessibilityMenu';
import Navigation from './components/navigation/Navigation';
import Footer from './components/common/Footer';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import PasswordReset from './pages/PasswordReset';
import AuthConfirm from './pages/AuthConfirm';
import Dashboard from './pages/Dashboard';
import Jobs from './pages/Jobs';
import Alliances from './pages/Alliances';
import { AuthProvider } from './context/AuthContext';
import { FeatureWrapper } from './components/FeatureWrapper';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ForgotPassword from './pages/ForgotPassword';
import Settings from './pages/Settings';
import { NotFound } from './components/common/NotFound';
import Freelancers from './pages/Freelancers';
import FreelancerProfile from './pages/FreelancerProfile';
import Notifications from './pages/Notifications';
import { PrivateRoute } from './components/PrivateRoute';
import Prospects from './pages/Prospects';
import JobSearchPage from './pages/JobSearchPage';
import RecruiterJobSearchRedirect from './components/jobs/RecruiterJobSearchRedirect';
import Quotes from './pages/Quotes';
import Projects from './pages/Projects';
import HubPage from './pages/HubPage';
import { ModalProvider } from './context/ModalContext';
import ReportList from './components/reports/ReportsList';
import AdminReviewList from './components/reviews/AdminReviewList';
import AdminLayout from './components/admin/layout/AdminLayout';
import { AdminRoute } from './components/admin/AdminRoute';
import AdminDashboard from './pages/AdminDashboard';
import ProfileList from './pages/ProfileList';
import AdminJobList from './pages/AdminJobList';
import AdminJobHubPage from './pages/AdminJobHubPage';
import CreateAdminPage from './pages/CreateAdminPage';
import AdminUserLookup from './pages/AdminUserLookup';
import IndustriesList from './pages/IndustriesList';
import CitiesList from './pages/CitiesList';
import ServiceCategoryList from './pages/ServiceCategoryList';
import BlogList from './pages/BlogList';
import BlogEditor from './pages/BlogEditor';
import BlogCategories from './pages/BlogCategories';
import BlogTags from './pages/BlogTags';
import CreditGiftConfigPage from './pages/CreditGiftConfigPage';
import AdminCreditTransactions from './pages/AdminCreditTransactions';
import WebsiteContentAdmin from './pages/WebsiteContentAdmin';
import ServiceCatalogAdmin from './pages/ServiceCatalogAdmin';
import CampaignsAdmin from './pages/CampaignsAdmin';
import WebsiteMediaAdmin from './pages/WebsiteMediaAdmin';
import PrintableBidPage from './pages/PrintableBidPage';

function LegacyQuoteRedirect() {
  const { jobId, quoteId } = useParams<{ jobId: string; quoteId: string }>();

  if (!jobId || !quoteId) {
    return <Navigate to="/projects" replace />;
  }

  return <Navigate to={`/projects/${jobId}?selectedQuoteId=${quoteId}`} replace />;
}

function LegacyAllianceQuoteRedirect() {
  const { jobId, quoteId, allianceQuoteId } = useParams<{
    jobId: string;
    quoteId: string;
    allianceQuoteId: string;
  }>();

  if (!jobId || !quoteId || !allianceQuoteId) {
    return <Navigate to="/projects" replace />;
  }

  return <Navigate to={`/projects/${jobId}?selectedQuoteId=${allianceQuoteId}`} replace />;
}

/** Redirects old path-based hub URLs to projects/:jobId?selectedQuoteId=... */
function LegacyHubPathRedirect() {
  const { jobId, principalQuoteId, allianceQuoteId } = useParams<{
    jobId: string;
    principalQuoteId?: string;
    allianceQuoteId?: string;
  }>();

  if (!jobId) {
    return <Navigate to="/projects" replace />;
  }

  const selectedQuoteId = allianceQuoteId ?? principalQuoteId ?? null;
  const search = selectedQuoteId ? `?selectedQuoteId=${selectedQuoteId}` : '';
  return <Navigate to={`/projects/${jobId}${search}`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <AnalyticsProvider>
              <ModalProvider>
                <ToastContainer position="top-right" autoClose={5000} />
                <Routes>
                  {/* === STANDALONE ROUTES (no nav/footer) === */}
                  <Route path="/quotes/:quoteId/print" element={<PrintableBidPage />} />

                  {/* === USER ROUTES === */}
                  <Route
                    path="/*"
                    element={
                      <div className="flex min-h-screen flex-col transition-colors duration-200">
                        <Navigation />
                        <main className="flex-1 px-4 pb-safe-bottom pt-safe-top">
                          <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/login" element={<Login />} />
                            <Route path="/signup" element={<Signup />} />
                            <Route path="/forgot-password" element={<ForgotPassword />} />
                            <Route path="/auth/confirm" element={<AuthConfirm />} />
                            <Route path="/reset-password" element={<PasswordReset />} />
                            <Route
                              path="/dashboard"
                              element={
                                <PrivateRoute>
                                  <Dashboard />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/jobs"
                              element={
                                <PrivateRoute
                                  activeKey="jobs"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.jobs' },
                                  ]}
                                >
                                  <Jobs />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/alliances"
                              element={
                                <PrivateRoute
                                  activeKey="alliances"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.alliances' },
                                  ]}
                                >
                                  <Alliances />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/prospects"
                              element={
                                <PrivateRoute
                                  activeKey="prospects"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.prospects' },
                                  ]}
                                >
                                  <Prospects />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/contacts"
                              element={
                                <PrivateRoute
                                  activeKey="contacts"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.contacts' },
                                  ]}
                                >
                                  <Prospects />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/quotes"
                              element={
                                <PrivateRoute
                                  activeKey="quotes"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.quotes' },
                                  ]}
                                >
                                  <Quotes />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/projects"
                              element={
                                <PrivateRoute
                                  activeKey="projects"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.projects' },
                                  ]}
                                >
                                  <Projects />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/projects/:jobId"
                              element={
                                <PrivateRoute
                                  activeKey="projects"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.projects' },
                                  ]}
                                >
                                  <HubPage />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/projects/:jobId/:principalQuoteId"
                              element={
                                <PrivateRoute activeKey="projects" breadCrumbs={[]}>
                                  <LegacyHubPathRedirect />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/projects/:jobId/:principalQuoteId/:allianceQuoteId"
                              element={
                                <PrivateRoute activeKey="projects" breadCrumbs={[]}>
                                  <LegacyHubPathRedirect />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/quotes/:jobId/:quoteId"
                              element={<LegacyQuoteRedirect />}
                            />
                            <Route
                              path="/quotes/:jobId/:quoteId/:allianceQuoteId"
                              element={<LegacyAllianceQuoteRedirect />}
                            />
                            <Route
                              path="/settings"
                              element={
                                <PrivateRoute
                                  activeKey="settings"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.settings' },
                                  ]}
                                >
                                  <Settings />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/notifications"
                              element={
                                <PrivateRoute
                                  activeKey="notifications"
                                  breadCrumbs={[
                                    { key: 'dashboard.title', route: '/dashboard' },
                                    { key: 'breadcrumbs.notifications' },
                                  ]}
                                >
                                  <Notifications />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/freelancers"
                              element={
                                <PrivateRoute
                                  activeKey="freelancers"
                                  breadCrumbs={[
                                    { key: 'breadcrumbs.find' },
                                    { key: 'breadcrumbs.freelancer' },
                                  ]}
                                >
                                  <Freelancers />
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/job-search"
                              element={
                                <PrivateRoute
                                  activeKey="projects"
                                  breadCrumbs={[
                                    { key: 'breadcrumbs.find' },
                                    { key: 'breadcrumbs.jobs' },
                                  ]}
                                >
                                  <RecruiterJobSearchRedirect>
                                    <JobSearchPage />
                                  </RecruiterJobSearchRedirect>
                                </PrivateRoute>
                              }
                            />
                            <Route
                              path="/freelancers/:freelancerId"
                              element={
                                <PrivateRoute
                                  activeKey="freelancers"
                                  breadCrumbs={[
                                    { key: 'breadcrumbs.find', route: '/freelancers' },
                                    { key: 'breadcrumbs.freelancer', route: '/freelancers' },
                                  ]}
                                  showMobileBreadCrumbs
                                >
                                  <FreelancerProfile />
                                </PrivateRoute>
                              }
                            />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </main>
                        <Footer />
                        <FeatureWrapper feature="accessibilityMenu">
                          <AccessibilityMenu />
                        </FeatureWrapper>
                      </div>
                    }
                  />

                  {/* === ROUTES ADMIN === */}
                  <Route element={<AdminLayout />}>
                    <Route
                      index
                      path="/admin"
                      element={
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="admin/reports"
                      element={
                        <AdminRoute>
                          <ReportList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/settings"
                      element={
                        <AdminRoute>
                          <Settings />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/notifications"
                      element={
                        <AdminRoute>
                          <Notifications />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/reviews"
                      element={
                        <AdminRoute>
                          <AdminReviewList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/freelancers"
                      element={
                        <AdminRoute>
                          <Freelancers />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/users"
                      element={
                        <AdminRoute>
                          <ProfileList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/jobs"
                      element={
                        <AdminRoute>
                          <AdminJobList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/jobs/:jobId/hub"
                      element={
                        <AdminRoute>
                          <AdminJobHubPage />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/services"
                      element={
                        <AdminRoute>
                          <ServiceCategoryList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/industries"
                      element={
                        <AdminRoute>
                          <IndustriesList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/cities"
                      element={
                        <AdminRoute>
                          <CitiesList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/freelancers/:freelancerId"
                      element={
                        <AdminRoute>
                          <FreelancerProfile />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/create"
                      element={
                        <AdminRoute>
                          <CreateAdminPage />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/user-lookup"
                      element={
                        <AdminRoute>
                          <AdminUserLookup />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/credits"
                      element={
                        <AdminRoute>
                          <CreditGiftConfigPage />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/credit-transactions"
                      element={
                        <AdminRoute>
                          <AdminCreditTransactions />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/blog"
                      element={
                        <AdminRoute>
                          <BlogList />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/blog/new"
                      element={
                        <AdminRoute>
                          <BlogEditor />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/blog/:id"
                      element={
                        <AdminRoute>
                          <BlogEditor />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/blog/categories"
                      element={
                        <AdminRoute>
                          <BlogCategories />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/blog/tags"
                      element={
                        <AdminRoute>
                          <BlogTags />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/website-content"
                      element={
                        <AdminRoute>
                          <WebsiteContentAdmin />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/service-catalog"
                      element={
                        <AdminRoute>
                          <ServiceCatalogAdmin />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/campaigns"
                      element={
                        <AdminRoute>
                          <CampaignsAdmin />
                        </AdminRoute>
                      }
                    />

                    <Route
                      path="admin/website-media"
                      element={
                        <AdminRoute>
                          <WebsiteMediaAdmin />
                        </AdminRoute>
                      }
                    />

                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </ModalProvider>
            </AnalyticsProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
